/**
 * /jobs/categories — Job Categories list page.
 * Legacy parity: list with ID, Title, Description columns.
 * Actions: Edit (→ /jobs/categories/:id), Delete (ConfirmDialog).
 * Header: Add category (→ /jobs/categories/new), back to Jobs.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Search, SlidersHorizontal, Tag } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardBody,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Input,
  Skeleton,
} from '@vnetwork/ui/primitives';
import {
  DataTable,
  EmptyState,
  Pagination,
  type ColumnDef,
} from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  IconButton,
} from '@vnetwork/ui/primitives';
import { paginationView } from '@/pages/customers/pagination-summary';
import { useJobCategories, useDeleteJobCategory, type JobCategory } from '@/api/jobs';
import { formatDateTime } from '@/lib/format';

/* ── Constants ──────────────────────────────────────────────────────────── */

const PAGE_SIZE = 20;

/* ── Helpers ────────────────────────────────────────────────────────────── */

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/* ── Columns ────────────────────────────────────────────────────────────── */

type CatCol = ColumnDef<JobCategory, unknown>;

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;

function buildCategoryColumns(cbs: {
  onEdit: (row: JobCategory) => void;
  onDelete: (row: JobCategory) => void;
}): CatCol[] {
  return [
    {
      id: 'id',
      header: 'ID',
      cell: ({ row }) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.original.id}</span>
      ),
    },
    {
      id: 'title',
      header: 'Title',
      cell: ({ row }) => (
        <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
          {row.original.title}
        </span>
      ),
    },
    {
      id: 'description',
      header: 'Description',
      cell: ({ row }) => {
        const d = row.original.description;
        if (!d) return <span style={muted}>—</span>;
        return (
          <span
            title={d}
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 320,
            }}
          >
            {d}
          </span>
        );
      },
    },
    {
      id: 'created_at',
      header: 'Created',
      cell: ({ row }) => (
        <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
          {formatDateTime(row.original.created_at)}
        </span>
      ),
    },
    {
      id: 'updated_at',
      header: 'Updated',
      cell: ({ row }) => (
        <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
          {formatDateTime(row.original.updated_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const cat = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton size="sm" variant="ghost" aria-label="Row actions">
                <MoreHorizontal size={15} />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => cbs.onEdit(cat)}>
                <Pencil size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem tone="danger" onSelect={() => cbs.onDelete(cat)}>
                <Trash2 size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];
}

/* ── Component ──────────────────────────────────────────────────────────── */

export default function JobCategoriesPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);
  const [deleteTarget, setDeleteTarget] = useState<JobCategory | null>(null);
  const [visible, setVisible] = useState<Record<string, boolean>>({});

  const { data, isLoading, isFetching, isError, refetch } = useJobCategories({
    page,
    page_size: PAGE_SIZE,
    q: query || undefined,
  });

  const rows = data?.results ?? [];
  const pagination = data?.pagination;
  const pageCount = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? rows.length;

  const deleteMut = useDeleteJobCategory();

  const allColumns = useMemo(
    () =>
      buildCategoryColumns({
        onEdit: (row) => navigate(`/jobs/categories/${row.id}`),
        onDelete: (row) => setDeleteTarget(row),
      }),
    [navigate],
  );

  const colId = (c: CatCol) =>
    typeof c.id === 'string' ? c.id : String(c.header ?? '');

  useEffect(() => {
    setVisible((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      return Object.fromEntries(allColumns.map((c) => [colId(c), true]));
    });
  }, [allColumns]);

  const visibleColumns = useMemo(
    () => allColumns.filter((c) => visible[colId(c)] !== false),
    [allColumns, visible],
  );

  const onSearch = (value: string) => {
    setRawQuery(value);
    setPage(1);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMut.mutateAsync(deleteTarget.id);
      toast.success(`Category "${deleteTarget.title}" deleted.`);
    } catch {
      toast.error('Failed to delete category.');
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <Container size="full">
      <PageHeader
        title="Job Categories"
        description="Manage categories used to organise job postings."
        style={{ marginBottom: 'var(--vn-space-4)' }}
        actions={
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              startIcon={<ArrowLeft size={15} />}
              onClick={() => navigate('/jobs')}
            >
              Back to Jobs
            </Button>
            <Button
              startIcon={<Plus size={15} />}
              onClick={() => navigate('/jobs/categories/new')}
            >
              Add category
            </Button>
          </div>
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Toolbar
          style={{ marginBottom: 0 }}
          start={
            <Input
              value={rawQuery}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search categories…"
              startSlot={<Search size={15} />}
              style={{ maxWidth: 320 }}
            />
          }
          end={
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary"
                  startIcon={<SlidersHorizontal size={15} />}
                >
                  Display options
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                {allColumns.map((c) => {
                  const id = colId(c);
                  return (
                    <DropdownMenuCheckboxItem
                      key={id}
                      checked={visible[id] !== false}
                      onCheckedChange={(v) =>
                        setVisible((prev) => ({ ...prev, [id]: v === true }))
                      }
                      onSelect={(e) => e.preventDefault()}
                    >
                      {typeof c.header === 'string' ? c.header : id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          }
        />

        {isError ? (
          <Alert
            tone="danger"
            title="Failed to load categories"
            actions={
              <Button size="sm" variant="ghost" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : isLoading ? (
          <Card flat>
            <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 24 }} />
              ))}
            </CardBody>
          </Card>
        ) : (
          <DataTable
            columns={visibleColumns}
            data={rows}
            getRowId={(r) => String(r.id)}
            onRowClick={(r) => navigate(`/jobs/categories/${r.id}`)}
            emptyState={
              <EmptyState
                icon={<Tag size={24} />}
                title="No categories found"
                description="Try adjusting your search or add a new category."
              />
            }
            footer={(() => {
              const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
              return (
                <Pagination
                  page={page}
                  pageCount={view.pageCount}
                  onPageChange={setPage}
                  summary={`${view.summary}${isFetching ? ' …' : ''}`}
                />
              );
            })()}
          />
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
        title="Delete category?"
        confirmLabel="Delete"
        destructive
        loading={deleteMut.isPending}
        onConfirm={handleDelete}
      >
        {`Delete "${deleteTarget?.title}"? This action cannot be undone.`}
      </ConfirmDialog>
    </Container>
  );
}
